import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthModal from '../components/auth/AuthModal';
import Logo from '../components/ui/Logo';
import useDarkMode from '../hooks/useDarkMode';
import '../styles/landing.css';

const LAST_UPDATED = '19 April 2026';

const SECTIONS = [
  { id: 'acceptance',    title: '1. Acceptance of terms' },
  { id: 'the-service',  title: '2. The service' },
  { id: 'accounts',     title: '3. Accounts' },
  { id: 'acceptable',   title: '4. Acceptable use' },
  { id: 'data',         title: '5. Your data' },
  { id: 'subscription', title: '6. Subscription & payment' },
  { id: 'availability', title: '7. Availability' },
  { id: 'liability',    title: '8. Limitation of liability' },
  { id: 'termination',  title: '9. Termination' },
  { id: 'changes',      title: '10. Changes to these terms' },
  { id: 'governing',    title: '11. Governing law' },
  { id: 'contact',      title: '12. Contact' },
];

export default function TermsOfServicePage() {
  const navigate                = useNavigate();
  const { isDark, toggleDark }  = useDarkMode();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState('signup');

  const openLogin  = () => { setAuthMode('login');  setAuthOpen(true); };
  const openSignup = () => { setAuthMode('signup'); setAuthOpen(true); };

  return (
    <div className="landing-root">

      {/* Nav */}
      <nav className="landing-nav">
        <a href="/"><Logo variant="full" size={32} /></a>
        <div className="landing-nav-actions">
          <button className="btn btn-sm landing-nav-theme" onClick={toggleDark} aria-label="Toggle dark mode">
            {isDark ? '☀️' : '🌙'}
          </button>
          <button className="btn btn-sm btn-outline-secondary" onClick={openLogin}>Log in</button>
          <button className="btn btn-sm btn-primary"            onClick={openSignup}>Sign up free</button>
        </div>
      </nav>

      {/* Page header */}
      <div className="docs-page-header">
        <div className="landing-container">
          <h1 className="docs-page-title">Terms of Service</h1>
          <p className="docs-page-sub">Last updated: {LAST_UPDATED}</p>
        </div>
      </div>

      {/* Layout: TOC + content */}
      <div className="docs-layout">

        {/* Sidebar TOC */}
        <aside className="docs-sidebar">
          <div className="docs-sidebar-group">
            <p className="docs-sidebar-label">Contents</p>
            {SECTIONS.map((s) => (
              <a key={s.id} href={`#${s.id}`} className="docs-sidebar-link">
                {s.title}
              </a>
            ))}
          </div>
        </aside>

        {/* Main content */}
        <main className="docs-content">

          <div className="docs-section" style={{ marginBottom: '0.5rem' }}>
            <p className="docs-para">
              These Terms of Service ("Terms") govern your use of Baby Tracker ("the service",
              "we", "us", "our"), operated by Kieron Harper, United Kingdom. By creating an
              account or using the service, you agree to be bound by these Terms. If you do not
              agree, do not use the service.
            </p>
          </div>

          {/* 1 */}
          <section id="acceptance" className="docs-section">
            <h2 className="docs-section-heading">1. Acceptance of terms</h2>
            <div className="docs-body">
              <p className="docs-para">
                By registering for an account, you confirm that you are at least 18 years old
                and have the legal capacity to enter into this agreement. If you are registering
                on behalf of a family or household, you accept these Terms on behalf of all users
                of your account.
              </p>
            </div>
          </section>

          {/* 2 */}
          <section id="the-service" className="docs-section">
            <h2 className="docs-section-heading">2. The service</h2>
            <div className="docs-body">
              <p className="docs-para">
                Baby Tracker is a child tracking application that allows parents and caregivers
                to log and monitor feeding, sleep, nappy changes, growth, health records,
                milestones, and more. The service is provided for personal, non-commercial use
                by families and their caregivers.
              </p>
              <p className="docs-para">
                <strong>Baby Tracker is not a medical device and does not provide medical advice.</strong>{' '}
                The information logged in the app is for personal record-keeping only. Always
                consult a qualified healthcare professional for medical decisions about your child.
              </p>
            </div>
          </section>

          {/* 3 */}
          <section id="accounts" className="docs-section">
            <h2 className="docs-section-heading">3. Accounts</h2>
            <div className="docs-body">
              <ul className="docs-list">
                <li>You are responsible for keeping your account credentials secure.</li>
                <li>You must provide a valid email address and keep it up to date.</li>
                <li>You are responsible for all activity that occurs under your account.</li>
                <li>You may invite other caregivers to access your child's profile. You are responsible for managing and revoking caregiver access as appropriate.</li>
                <li>You may delete your account at any time via the account settings. All associated data will be permanently deleted within 30 days.</li>
              </ul>
            </div>
          </section>

          {/* 4 */}
          <section id="acceptable" className="docs-section">
            <h2 className="docs-section-heading">4. Acceptable use</h2>
            <div className="docs-body">
              <p className="docs-para">You agree not to:</p>
              <ul className="docs-list">
                <li>Use the service for any unlawful purpose or in violation of any applicable laws</li>
                <li>Attempt to gain unauthorised access to other users' accounts or data</li>
                <li>Use the service to store data about a child without appropriate parental or guardian authority</li>
                <li>Attempt to reverse engineer, decompile, or exploit the service</li>
                <li>Use automated tools to scrape, extract, or abuse the service</li>
                <li>Impersonate another person or caregiver</li>
              </ul>
              <p className="docs-para">
                We reserve the right to suspend or terminate accounts that violate these terms
                without prior notice.
              </p>
            </div>
          </section>

          {/* 5 */}
          <section id="data" className="docs-section">
            <h2 className="docs-section-heading">5. Your data</h2>
            <div className="docs-body">
              <p className="docs-para">
                You own the data you enter into Baby Tracker. We do not claim any ownership over
                your tracking records, journal entries, or child profiles.
              </p>
              <p className="docs-para">
                By using the service, you grant us a limited licence to store and process your
                data solely for the purpose of providing the service to you.
              </p>
              <p className="docs-para">
                How we collect, use, and protect your data is described in our{' '}
                <a href="/privacy" style={{ color: 'var(--primary-dark)' }}>Privacy Policy</a>,
                which forms part of these Terms.
              </p>
              <p className="docs-para">
                You can export all your data or delete your account at any time via app settings.
              </p>
            </div>
          </section>

          {/* 6 */}
          <section id="subscription" className="docs-section">
            <h2 className="docs-section-heading">6. Subscription &amp; payment</h2>
            <div className="docs-body">
              <p className="docs-para">
                Core tracking features are free. A paid subscription tier is planned for advanced
                features. When subscription pricing is introduced, the following will apply:
              </p>
              <ul className="docs-list">
                <li>Subscription fees will be clearly displayed before any charge is made</li>
                <li>Subscriptions will renew automatically unless cancelled before the renewal date</li>
                <li>You can cancel your subscription at any time; access continues until the end of the paid period</li>
                <li>Refunds are handled in accordance with your statutory rights under UK consumer law</li>
              </ul>
              <p className="docs-para">
                Free features will remain free. We will give at least 30 days' notice before
                moving any currently free feature behind a paywall.
              </p>
            </div>
          </section>

          {/* 7 */}
          <section id="availability" className="docs-section">
            <h2 className="docs-section-heading">7. Availability</h2>
            <div className="docs-body">
              <p className="docs-para">
                We aim to provide a reliable, always-available service but cannot guarantee
                uninterrupted access. The service may be temporarily unavailable due to
                maintenance, infrastructure issues, or circumstances beyond our control.
              </p>
              <p className="docs-para">
                Baby Tracker is an offline-first app — core functionality is available even without
                an internet connection, with data syncing when connectivity is restored.
              </p>
            </div>
          </section>

          {/* 8 */}
          <section id="liability" className="docs-section">
            <h2 className="docs-section-heading">8. Limitation of liability</h2>
            <div className="docs-body">
              <p className="docs-para">
                To the fullest extent permitted by UK law, Baby Tracker and its operator shall
                not be liable for:
              </p>
              <ul className="docs-list">
                <li>Any indirect, incidental, or consequential loss arising from use of the service</li>
                <li>Loss of data due to circumstances beyond our reasonable control</li>
                <li>Any medical decisions made on the basis of information logged in the app</li>
                <li>Actions taken by caregivers you have granted access to your child's profile</li>
              </ul>
              <p className="docs-para">
                Nothing in these Terms limits our liability for death or personal injury caused
                by negligence, fraud, or any liability that cannot be excluded under UK law.
              </p>
              <p className="docs-para">
                Your statutory rights as a consumer under UK law are not affected by these Terms.
              </p>
            </div>
          </section>

          {/* 9 */}
          <section id="termination" className="docs-section">
            <h2 className="docs-section-heading">9. Termination</h2>
            <div className="docs-body">
              <p className="docs-para">
                <strong>By you:</strong> You may delete your account at any time via app settings.
                All your data will be permanently deleted within 30 days.
              </p>
              <p className="docs-para">
                <strong>By us:</strong> We may suspend or terminate your account if you breach
                these Terms, with or without notice depending on the severity of the breach.
              </p>
              <p className="docs-para">
                <strong>Service closure:</strong> If we ever close the service, we will give at
                least 90 days' notice by email, provide a data export tool, and pro-rate any
                unused subscription fees.
              </p>
            </div>
          </section>

          {/* 10 */}
          <section id="changes" className="docs-section">
            <h2 className="docs-section-heading">10. Changes to these terms</h2>
            <div className="docs-body">
              <p className="docs-para">
                We may update these Terms from time to time. We will notify you of material
                changes by email at least 14 days before they take effect. Continued use of
                the service after the effective date constitutes acceptance of the updated Terms.
              </p>
            </div>
          </section>

          {/* 11 */}
          <section id="governing" className="docs-section">
            <h2 className="docs-section-heading">11. Governing law</h2>
            <div className="docs-body">
              <p className="docs-para">
                These Terms are governed by the laws of England and Wales. Any disputes arising
                from these Terms or your use of the service shall be subject to the exclusive
                jurisdiction of the courts of England and Wales, without prejudice to your
                statutory rights as a UK consumer.
              </p>
            </div>
          </section>

          {/* 12 */}
          <section id="contact" className="docs-section">
            <h2 className="docs-section-heading">12. Contact</h2>
            <div className="docs-body">
              <p className="docs-para">
                For questions about these Terms, contact us at: <strong>[CONTACT EMAIL]</strong>
              </p>
            </div>
          </section>

        </main>
      </div>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-container landing-footer-inner">
          <Logo variant="full" size={28} />
          <nav className="landing-footer-links">
            <a href="/docs">Help</a>
            <a href="/roadmap">Roadmap</a>
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
            <button className="landing-footer-btn-link" onClick={openLogin}>Log in</button>
          </nav>
        </div>
      </footer>

      {authOpen && (
        <AuthModal
          initialMode={authMode}
          onClose={() => setAuthOpen(false)}
          onSuccess={() => { setAuthOpen(false); navigate('/app'); }}
        />
      )}
    </div>
  );
}
